import { cn } from '@/lib/utils';
import { Checkbox } from '@pms-digital/design-system';
import { NOYAU_CODES, type NoyauCode } from '@rosetta/shared';
import { infobulleNoyau, styleNoyau } from './noyaux-affichage';
import { PastilleNoyau } from './pastille-noyau';

interface ChoixNoyauxProps {
  /** The checked noyaux. */
  valeur: readonly NoyauCode[];
  onChange: (valeur: NoyauCode[]) => void;
  /** Locked while a translation is running. */
  verrouille?: boolean;
  /** Text before the buttons. */
  libelle?: string;
  /** Show the "tout sélectionner" checkbox after the buttons. */
  toutSelectionner?: boolean;
}

/** Keeps the charter order whatever the order of the clicks. */
function enOrdre(codes: ReadonlySet<NoyauCode>): NoyauCode[] {
  return NOYAU_CODES.filter((code) => codes.has(code));
}

/**
 * Multiple choice of the target noyaux (input screen): six checkbox buttons whose
 * frame holds the round dot and the code. Unchecked: grey frame, white background.
 * Checked: frame and tint in the colour of the noyau, no tick drawn. The grey frame and the
 * box of "tout sélectionner" (the `Checkbox` of the design system) are `gray-500` (3.4:1), the `gray-300` of the board is 1.9:1: below
 * the 3:1 that WCAG 1.4.11 asks for what shows the state of a control (S14, #5).
 */
export function ChoixNoyaux({
  valeur,
  onChange,
  verrouille = false,
  libelle = 'Traduire vers',
  toutSelectionner = true,
}: ChoixNoyauxProps) {
  const choisis = new Set(valeur);
  const tous = choisis.size === NOYAU_CODES.length;

  function basculer(code: NoyauCode) {
    const suivants = new Set(choisis);
    if (!suivants.delete(code)) suivants.add(code);
    onChange(enOrdre(suivants));
  }

  function basculerTous() {
    onChange(tous ? [] : [...NOYAU_CODES]);
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className={cn('pr-2 font-semibold text-small', verrouille && 'text-gray-600')}>
        {libelle}
      </span>
      {/* biome-ignore lint/a11y/useSemanticElements: a fieldset would add a frame and a legend the mockups do not have */}
      <div role="group" aria-label="noyaux cibles" className="flex flex-wrap items-center gap-2">
        {NOYAU_CODES.map((code) => {
          const coche = choisis.has(code);
          const style = styleNoyau(code);
          return (
            <button
              key={code}
              type="button"
              // biome-ignore lint/a11y/useSemanticElements: framed toggle button, the ARIA checkbox pattern of the mockups
              role="checkbox"
              aria-checked={coche}
              aria-label={infobulleNoyau(code)}
              title={infobulleNoyau(code)}
              disabled={verrouille}
              onClick={() => basculer(code)}
              className={cn(
                // 2 px frame in every state: only its colour changes, so the button never moves
                // or grows, and the fill stops at the frame (`bg-clip-padding`).
                'inline-flex h-10 items-center gap-2 rounded-md border-2 bg-clip-padding px-3 transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-50',
                coche
                  ? [style.cadre, style.fond, !verrouille && style.fondSurvol]
                  : 'border-gray-500 bg-white enabled:hover:bg-gray-100',
              )}
            >
              <PastilleNoyau noyau={code} />
              <span
                className={cn(
                  'font-bold text-small leading-none tracking-[1px]',
                  coche ? 'text-black' : 'text-gray-700',
                )}
              >
                {code}
              </span>
            </button>
          );
        })}
      </div>
      {toutSelectionner ? (
        <>
          <span aria-hidden="true" className="h-6 border-gray-200 border-l" />
          <Checkbox
            label="tout sélectionner"
            checked={tous}
            disabled={verrouille}
            onCheckedChange={basculerTous}
          />
        </>
      ) : null}
    </div>
  );
}
